import Vue from "vue";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../fb";
import { EventBus, events } from "../event-bus";

/**
 * Laufende Bild-Edits für den Header (Malte 16.10.): Bereichs-Edits (kind 'region') und Feedback-/KI-Edits
 * (kind 'feedback') laufen als Kind-Jobs in upscaleJobs. Live abonniert werden NUR die laufenden
 * (kind in [...] und status in [queued, running] — ohne Composite-Index möglich, winziger Datensatz).
 * Fällt ein Job aus der Abfrage, wird er einmal nachgelesen und landet mit Ergebnis in `finished`
 * (2 h im Browser gespeichert, damit ein Neuladen nichts verliert).
 */
const KINDS = ["region", "feedback"];
const STORE_KEY = "taskdash_edit_jobs_v1";
const KEEP_MS = 2 * 3600 * 1000;

const load = () => {
  try {
    const s = JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
    return { tracked: s.tracked || {}, finished: (s.finished || []).filter((f) => Date.now() - (f.finishedAtMs || 0) < KEEP_MS), seen: s.seen || {}, ackAt: Number(s.ackAt) || 0 };
  } catch (e) {
    return { tracked: {}, finished: [], seen: {}, ackAt: 0 };
  }
};
const saved = load();

export const editJobs = Vue.observable({
  running: [],
  finished: saved.finished,
  seen: saved.seen,
  /** Header-Badge „fertig/gestartet" zählt nur, was nach dem letzten Öffnen des Menüs fertig wurde */
  ackAt: saved.ackAt,
  available: true, // false = keine Leserechte auf upscaleJobs
  orderNames: {},
});
let tracked = saved.tracked; // id → { by, taskUid, label } der zuletzt laufenden Jobs
let unsub = null;

const persist = () => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ tracked, finished: editJobs.finished.slice(0, 60), seen: editJobs.seen, ackAt: editJobs.ackAt }));
  } catch (e) {
    /* voll */
  }
};

const toMs = (v) => (v && typeof v.toMillis === "function" ? v.toMillis() : v ? Date.parse(v) || 0 : 0);
export const orderUidOf = (job) => String((job && job.taskUid) || "").split("_")[0];

/** Kompakte Form für Liste/Speicher (ohne große Felder) */
const slim = (id, d) => ({
  id,
  kind: d.kind,
  label: d.label || (d.kind === "region" ? "Bereichs-Edit" : "Feedback-Edit"),
  text: String(d.instruction || d.comment || "").split("\n")[0].slice(0, 140),
  by: d.by || null,
  taskUid: d.taskUid || null,
  status: d.status,
  step: d.step || null,
  error: d.error ? String(d.error).slice(0, 200) : null,
  scoreAfter: d.result && d.result.scoreAfter != null ? Number(d.result.scoreAfter) : null,
  warning: (d.result && d.result.warning) || null,
  createdAtMs: toMs(d.createdAt),
  startedAtMs: toMs(d.startedAt),
  finishedAtMs: toMs(d.finishedAt) || Date.now(),
});

async function ensureOrderName(job) {
  const uid = orderUidOf(job);
  if (!uid || editJobs.orderNames[uid] !== undefined) return;
  Vue.set(editJobs.orderNames, uid, null);
  try {
    const snap = await getDoc(doc(db, "orders", uid));
    Vue.set(editJobs.orderNames, uid, (snap.exists() && snap.data().orderName) || uid);
  } catch (e) {
    Vue.set(editJobs.orderNames, uid, uid); // keine Leserechte auf orders → Bestell-ID zeigen
  }
}

async function settle(id) {
  try {
    const snap = await getDoc(doc(db, "upscaleJobs", id));
    delete tracked[id];
    if (!snap.exists()) return persist();
    const d = snap.data();
    if (d.status === "queued" || d.status === "running") return persist();
    const item = slim(id, d);
    editJobs.finished = [item, ...editJobs.finished.filter((f) => f.id !== id)].slice(0, 60);
    ensureOrderName(item);
    persist();
    const me = auth.currentUser && auth.currentUser.uid;
    if (me && item.by === me) {
      const name = editJobs.orderNames[orderUidOf(item)] || orderUidOf(item);
      EventBus.$emit(events.SNACKBAR, item.status === "done"
        ? { message: `Edit fertig: ${name} · ${item.label}`, type: "success" }
        : { message: `Edit fehlgeschlagen: ${name} · ${item.label}`, type: "error" });
    }
  } catch (e) {
    console.warn("Edit-Job nachlesen:", e.message);
  }
}

/** Idempotent; einmal aus dem Header-Knopf starten */
export function startEditJobs() {
  if (unsub) return;
  const q = query(collection(db, "upscaleJobs"), where("kind", "in", KINDS), where("status", "in", ["queued", "running"]));
  // Jobs, die beim letzten Besuch liefen und inzwischen fertig sind, nachlesen
  const stale = Object.keys(tracked);
  unsub = onSnapshot(
    q,
    (snap) => {
      editJobs.available = true;
      const now = snap.docs.map((d) => slim(d.id, d.data()));
      const nowIds = new Set(now.map((j) => j.id));
      for (const id of Object.keys(tracked)) if (!nowIds.has(id)) settle(id);
      tracked = Object.fromEntries(now.map((j) => [j.id, { by: j.by, taskUid: j.taskUid, label: j.label }]));
      editJobs.running = now.sort((a, b) => b.createdAtMs - a.createdAtMs);
      now.forEach(ensureOrderName);
      persist();
    },
    (err) => {
      editJobs.available = false;
      console.warn("Edit-Jobs:", err.message);
    }
  );
  stale.forEach((id) => setTimeout(() => !editJobs.running.some((j) => j.id === id) && settle(id), 1500));
  editJobs.finished.forEach(ensureOrderName);
}

export function markSeen(id) {
  Vue.set(editJobs.seen, id, Date.now());
  persist();
}
/** Menü geöffnet/geschlossen: fertige Edits gelten für das Header-Badge als gesehen */
export function ackEdits() {
  editJobs.ackAt = Date.now();
  persist();
}
export function markAllSeen() {
  editJobs.finished.forEach((f) => Vue.set(editJobs.seen, f.id, Date.now()));
  persist();
}
